<script setup lang="ts">
import { ref, watch, computed, onUnmounted } from "vue";
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "./stores/auth";
import { isNative } from "./lib/platform";
import { useI18n } from "./composables/useI18n";
import { APP_VERSION } from "./version";
import { subscribeAppVersion, forceReload, type VersionState } from "./lib/versionGate";
import NavIcon from "./components/NavIcon.vue";
import ActivityDrawer from "./components/ActivityDrawer.vue";

const auth = useAuthStore();
const { t, locale, setLocale } = useI18n();
const router = useRouter();
const route = useRoute();

const mobileMenuOpen = ref(false);

/**
 * True when running inside the Android/iOS Capacitor wrapper.
 * Used to hide subscription-related UI (Plan tab, Pro teaser,
 * external links to maxglasstrack.com) per Google Play policy
 * which forbids steering users to external payment systems for
 * digital content.
 *
 * On web (Capacitor returns false), everything stays visible —
 * Stripe checkout works normally there.
 */
const isNativeApp = computed(() => isNative());

// Close menu when navigating
watch(() => route.fullPath, () => { mobileMenuOpen.value = false; });

async function handleSignOut() {
  mobileMenuOpen.value = false;
  await auth.signOut();
  router.push({ name: "login" });
}

function toggleLocale() {
  setLocale(locale.value === "fr" ? "en" : "fr");
}

// ============================================================
// Pending account deletion (v1.12.53+)
// ============================================================
//
// Si l'user revient pendant les 30j de grace period (deletedAt
// set), on bloque toute l'app avec une modal qui lui demande
// soit d'annuler soit de continuer la suppression.
//
// "Continuer" = sign out (la modal disparait, deletedAt reste
// set côté serveur, le purge cron va clean après les 30j). Au
// prochain login, la modal réapparaît.
//
// "Annuler" = appel CF cancelAccountDeletion → clear deletedAt
// + reactivate Stripe sub si nécessaire.

const pendingDeletionBusy = ref(false);
const pendingDeletionError = ref("");

const pendingDeletionDateLabel = computed(() => {
  const d = auth.accountDeletionEffectiveDate;
  if (!d) return "";
  // Format "9 juin 2026" / "June 9, 2026"
  return d.toLocaleDateString(
    locale.value === "fr" ? "fr-CA" : "en-CA",
    { year: "numeric", month: "long", day: "numeric" }
  );
});

async function onCancelDeletion() {
  pendingDeletionBusy.value = true;
  pendingDeletionError.value = "";
  try {
    const { httpsCallable } = await import("firebase/functions");
    const { functions } = await import("./firebase");
    const callable = httpsCallable<Record<string, never>, { success: boolean }>(
      functions,
      "cancelAccountDeletion"
    );
    const res = await callable({});
    if (!res.data.success) {
      throw new Error("Server returned non-success");
    }
    // The onSnapshot watcher in the auth store will pick up the
    // cleared deletedAt and the modal will disappear automatically.
  } catch (e: unknown) {
    pendingDeletionError.value =
      e instanceof Error ? e.message : t("account.pendingCancelError");
  } finally {
    pendingDeletionBusy.value = false;
  }
}

async function onContinueDeletion() {
  // Q5=(a) — user confirms they want to be deleted, sign them
  // out. The deletedAt remains set, the cron will purge after 30j.
  // If they change their mind during those 30 days, they can sign
  // back in and the modal will reappear with another chance to cancel.
  pendingDeletionBusy.value = true;
  try {
    await auth.signOut();
    router.push({ name: "login" });
  } finally {
    pendingDeletionBusy.value = false;
  }
}

// ============================================================
// Gate de version (v1.22.8) — force la mise à jour des bundles périmés
// ============================================================
// La lecture de system/appVersion exige d'être connecté (règle Firestore),
// donc on (dé)s'abonne selon l'état de connexion. Fail-open : toute erreur
// laisse l'app fonctionner normalement.
const versionBlocked = ref(false);      // min serveur > version embarquée
const updateAvailable = ref(false);     // current serveur > version embarquée
const serverVersion = ref("");
const updateDismissed = ref(false);     // bandeau doux masqué par l'user
const reloading = ref(false);

let unsubVersion: (() => void) | null = null;

watch(
  () => auth.isSignedIn,
  (signedIn) => {
    if (signedIn && !unsubVersion) {
      unsubVersion = subscribeAppVersion((s: VersionState) => {
        versionBlocked.value = s.blocked;
        updateAvailable.value = s.updateAvailable;
        serverVersion.value = s.serverVersion;
      });
    } else if (!signedIn && unsubVersion) {
      unsubVersion();
      unsubVersion = null;
      versionBlocked.value = false;
      updateAvailable.value = false;
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (unsubVersion) unsubVersion();
});

async function doForceReload() {
  reloading.value = true;
  await forceReload();
}
</script>

<template>
  <div class="app-root">
    <header v-if="auth.isSignedIn" class="app-header">
      <!-- Left: logo (clickable) -->
      <RouterLink to="/quotes" class="brand" :title="t('nav.quotes')">
        <img src="/logo-white.svg" alt="MaxGlassTrack" class="brand-logo" />
        <span class="version">v{{ APP_VERSION }}</span>
      </RouterLink>

      <!-- Desktop nav - hidden on mobile -->
      <nav class="app-nav desktop-only">
        <RouterLink to="/quotes">{{ t("nav.quotes") }}</RouterLink>
        <!-- v1.12.57 — Vitres + Stats toujours visibles : ce sont des
             entrées de prix manuelles + analyse, indépendantes du
             scraper automatique. Le flag `gpm` ne contrôle QUE le
             scraper (bouton GPM dans QuoteEditor + onglets credentials
             /workers Settings). -->
        <RouterLink to="/parebrises">{{ t("nav.priceHistory") }}</RouterLink>
        <RouterLink to="/reclamations">{{ t("nav.reclamations") }}</RouterLink>
        <RouterLink to="/stats">{{ t("nav.stats") }}</RouterLink>
        <RouterLink to="/settings">{{ t("nav.settings") }}</RouterLink>
        <!-- v1.12.56 — Onglet "Plan" caché côté Android (Google Play interdit
             le steering vers paiement externe). Visible web pour Stripe. -->
        <RouterLink v-if="!isNativeApp" to="/billing">
          {{ t("nav.billing") }}
          <span class="plan-badge" :class="auth.isPro ? 'pro' : 'free'">
            {{ auth.isPro ? t("nav.planPro") : t("nav.planFree") }}
          </span>
        </RouterLink>
      </nav>

      <!-- Desktop user menu - hidden on mobile -->
      <div class="user-menu desktop-only">
        <button class="locale-btn" @click="toggleLocale" :title="t('settings.language')">
          {{ locale === "fr" ? "🇫🇷 FR" : "🇬🇧 EN" }}
        </button>
        <img v-if="auth.user?.photoURL" :src="auth.user.photoURL" class="avatar" />
        <span class="name">{{ auth.user?.displayName }}</span>
        <button class="btn-icon" @click="handleSignOut">{{ t("auth.signOut") }}</button>
      </div>

      <!-- Mobile quick-access icons : 4 main destinations as compact
           buttons in the header. Each one is a RouterLink that the
           browser highlights with the `.router-link-active` class when
           the current route matches — we use that to switch the icon
           color (white -> accent blue) via CSS. -->
      <nav class="mobile-quick-nav mobile-only" :aria-label="t('nav.menu')">
        <RouterLink to="/quotes" class="quick-icon" :title="t('nav.quotes')">
          <NavIcon name="quotes" :size="22" />
        </RouterLink>
        <!-- v1.12.57 — icônes Vitres + Stats toujours visibles (entrées manuelles). -->
        <RouterLink to="/parebrises" class="quick-icon" :title="t('nav.priceHistory')">
          <NavIcon name="glass" :size="22" />
        </RouterLink>
        <RouterLink to="/reclamations" class="quick-icon" :title="t('nav.reclamations')">
          <i class="ti ti-clipboard-list" style="font-size: 22px;"></i>
        </RouterLink>
        <RouterLink to="/stats" class="quick-icon" :title="t('nav.stats')">
          <NavIcon name="prices" :size="22" />
        </RouterLink>
        <RouterLink to="/settings" class="quick-icon" :title="t('nav.settings')">
          <NavIcon name="settings" :size="22" />
        </RouterLink>
      </nav>

      <!-- Mobile hamburger - only shown on mobile -->
      <button
        class="mobile-menu-btn mobile-only"
        @click="mobileMenuOpen = !mobileMenuOpen"
        :aria-label="t('nav.menu')"
      >
        <svg v-if="!mobileMenuOpen" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
        <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="6" y1="6" x2="18" y2="18"/>
          <line x1="6" y1="18" x2="18" y2="6"/>
        </svg>
      </button>
    </header>

    <!-- Mobile drawer menu -->
    <div v-if="mobileMenuOpen && auth.isSignedIn" class="mobile-drawer mobile-only" @click.self="mobileMenuOpen = false">
      <div class="mobile-drawer-content">
        <nav class="mobile-nav">
          <RouterLink to="/quotes">{{ t("nav.quotes") }}</RouterLink>
          <!-- v1.12.57 — Vitres + Stats toujours visibles (entrées manuelles). -->
          <RouterLink to="/parebrises">{{ t("nav.priceHistory") }}</RouterLink>
          <RouterLink to="/reclamations">{{ t("nav.reclamations") }}</RouterLink>
          <RouterLink to="/stats">{{ t("nav.stats") }}</RouterLink>
          <RouterLink to="/settings">{{ t("nav.settings") }}</RouterLink>
          <!-- v1.12.56 — Plan caché Android (Google Play steering policy). -->
          <RouterLink v-if="!isNativeApp" to="/billing">
            {{ t("nav.billing") }}
            <span class="plan-badge" :class="auth.isPro ? 'pro' : 'free'">
              {{ auth.isPro ? t("nav.planPro") : t("nav.planFree") }}
            </span>
          </RouterLink>
        </nav>

        <div class="mobile-footer">
          <div class="mobile-user-info">
            <img v-if="auth.user?.photoURL" :src="auth.user.photoURL" class="avatar" />
            <span class="name">{{ auth.user?.displayName || auth.user?.email }}</span>
          </div>
          <div class="mobile-actions">
            <button class="locale-btn" @click="toggleLocale">
              {{ locale === "fr" ? "🇫🇷 FR" : "🇬🇧 EN" }}
            </button>
            <button class="btn-signout" @click="handleSignOut">
              {{ t("auth.signOut") }}
            </button>
          </div>
          <RouterLink to="/privacy" class="footer-privacy-link">
            {{ locale === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy' }}
          </RouterLink>
        </div>
      </div>
    </div>

    <main class="app-main">
      <!-- Pending deletion modal (v1.12.53+) :
           Si l'user a `deletedAt` set sur son profil, on affiche
           une modal bloquante qui couvre toute l'app. Il doit
           soit annuler la suppression, soit confirmer (sign out).
           Aucun accès à l'app ne lui est laissé pendant ce temps.
           Q5=(a) — modal bloquante, Q6=(b) — peut annuler quand
           il veut pendant les 30j. -->
      <!-- Gate de version : blocage DUR. Un bundle plus vieux que `min`
           ne peut plus tourner (ex : écritures directes d'avant v1.16
           bloquées par les règles → « permissions insuffisantes »).
           Modal non fermable, seule action = recharger (cache-bust). -->
      <div v-if="versionBlocked" class="modal-overlay version-gate-overlay">
        <div class="modal-content version-gate-modal">
          <h3>{{ t("update.requiredTitle") }}</h3>
          <p>{{ t("update.requiredBody") }}</p>
          <p class="version-gate-meta">
            v{{ APP_VERSION }}<span v-if="serverVersion"> → v{{ serverVersion }}</span>
          </p>
          <div class="modal-actions">
            <button class="btn-primary" @click="doForceReload" :disabled="reloading">
              {{ reloading ? t("update.reloading") : t("update.reload") }}
            </button>
          </div>
        </div>
      </div>

      <!-- Gate de version : bandeau DOUX. Nouvelle version dispo mais pas
           obligatoire — l'user peut recharger ou remettre à plus tard. -->
      <div
        v-if="updateAvailable && !versionBlocked && !updateDismissed"
        class="update-banner"
      >
        <span class="update-banner-text">{{ t("update.availableText") }}</span>
        <button class="update-banner-reload" @click="doForceReload" :disabled="reloading">
          {{ reloading ? t("update.reloading") : t("update.reloadShort") }}
        </button>
        <button class="update-banner-dismiss" @click="updateDismissed = true" :aria-label="t('update.later')">
          ✕
        </button>
      </div>

      <div v-if="auth.isAccountDeletionPending && auth.isSignedIn" class="modal-overlay pending-deletion-overlay">
        <div class="modal-content pending-deletion-modal">
          <h3>{{ t("account.pendingTitle") }}</h3>
          <p>
            {{ t("account.pendingBody", { date: pendingDeletionDateLabel }) }}
          </p>
          <div v-if="pendingDeletionError" class="msg error-msg-inline">
            {{ pendingDeletionError }}
          </div>
          <div class="modal-actions">
            <button
              class="btn-danger"
              @click="onContinueDeletion"
              :disabled="pendingDeletionBusy"
            >
              {{ t("account.pendingContinue") }}
            </button>
            <button
              class="btn"
              @click="onCancelDeletion"
              :disabled="pendingDeletionBusy"
            >
              {{ pendingDeletionBusy ? "…" : t("account.pendingCancel") }}
            </button>
          </div>
        </div>
      </div>

      <RouterView v-if="!auth.loading && !auth.isAccountDeletionPending" />
      <div v-else-if="auth.loading" class="loading">{{ t("common.loading") }}</div>
    </main>

    <!-- Journal d'activités : onglet vertical bord droit, présent partout -->
    <ActivityDrawer v-if="auth.isSignedIn && !auth.isAccountDeletionPending" />
  </div>
</template>

<style scoped>
.version {
  font-size: 10px;
  color: var(--text-dim);
  margin-left: 8px;
  font-weight: 400;
  font-family: 'JetBrains Mono', monospace;
}

/* ── Gate de version ── */
/* Blocage dur : au-dessus de tout (y compris la modal de suppression). */
.version-gate-overlay {
  z-index: 100000;
}
.version-gate-modal {
  text-align: center;
  max-width: 420px;
}
.version-gate-meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: var(--text-dim);
  margin-top: 4px;
}
/* Bandeau doux : barre fixe en bas, discrète mais visible. */
.update-banner {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 9000;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px 10px 16px;
  background: var(--surface2, #1a2130);
  border: 1px solid var(--border, #2a3244);
  border-radius: var(--radius-md, 10px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}
.update-banner-text {
  font-size: 14px;
  color: var(--text, #e8ecf4);
}
.update-banner-reload {
  padding: 6px 14px;
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: var(--accent, #3b82f6);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
.update-banner-reload:disabled {
  opacity: 0.6;
  cursor: default;
}
.update-banner-dismiss {
  background: transparent;
  border: none;
  color: var(--text-dim, #8a93a6);
  font-size: 14px;
  cursor: pointer;
  padding: 4px 6px;
}
.locale-btn {
  padding: 6px 10px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
}
.locale-btn:hover { background: var(--surface); }

/* ===== MOBILE: hamburger button ===== */
.mobile-menu-btn {
  background: transparent;
  border: none;
  color: var(--text);
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
}
.mobile-menu-btn:hover { background: var(--surface2); }

/* ===== MOBILE: quick-access icon row in header ===== */
/* Sits between the brand (logo + MaxGlassTrack) and the hamburger.
   Each icon is a RouterLink — vue-router auto-applies `.router-link-active`
   when the current route matches, which we use to switch the icon color. */
.mobile-quick-nav {
  margin-left: auto;            /* push icons + hamburger to the right */
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: nowrap;
}
.quick-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.55); /* default = muted white */
  text-decoration: none;
  transition: color 0.15s, background 0.15s;
  flex-shrink: 0;
}
.quick-icon:hover {
  color: rgba(255, 255, 255, 0.85);
  background: var(--surface2);
}
/* Active state: blue tint background + accent text color (which the
   NavIcon picks up via currentColor). vue-router exposes both classes;
   we use `.router-link-active` because our routes don't have nested
   children that would trigger false-positives with the exact variant. */
.quick-icon.router-link-active {
  color: var(--accent);
  background: rgba(56, 189, 248, 0.18);
}

/* ===== MOBILE DRAWER ===== */
.mobile-drawer {
  position: fixed;
  inset: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: flex;
  justify-content: flex-end;
}
.mobile-drawer-content {
  width: 82%;
  max-width: 320px;
  height: 100dvh;
  background: var(--surface);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  padding: 74px 0 0 0;
  animation: slide-in 0.2s ease;
}
@keyframes slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.mobile-nav {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 8px;
}
.mobile-nav a {
  padding: 16px 20px;
  color: var(--text);
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mobile-nav a:hover,
.mobile-nav a:active { background: var(--surface2); }
.mobile-nav a.router-link-active {
  background: rgba(79, 172, 254, 0.15);
  color: var(--accent);
}
.mobile-footer {
  border-top: 1px solid var(--border);
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.mobile-user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: var(--text-muted);
  overflow: hidden;
}
.mobile-user-info .name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mobile-user-info .avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}
.mobile-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.mobile-actions .locale-btn { flex-shrink: 0; }
.footer-privacy-link {
  font-size: 11px;
  color: var(--text-dim);
  text-decoration: none;
  text-align: center;
  margin-top: 4px;
}
.footer-privacy-link:hover {
  color: var(--accent);
  text-decoration: underline;
}
.btn-signout {
  flex: 1;
  padding: 10px 14px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}
.btn-signout:hover { background: var(--red); color: #fff; border-color: var(--red); }

/* ===== RESPONSIVE VISIBILITY ===== */
.mobile-only { display: none; }
.desktop-only { display: flex; }

@media (max-width: 768px) {
  .mobile-only { display: flex; }
  .desktop-only { display: none !important; }
}

/* ===========================================================
   Pending account deletion modal (v1.12.53+)
   =========================================================== */
.pending-deletion-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.pending-deletion-modal {
  background: var(--surface, #1a1f2a);
  border: 1px solid rgba(220, 38, 38, 0.4);
  border-radius: var(--radius, 8px);
  padding: 24px;
  max-width: 480px;
  width: 100%;
  color: var(--text, #e8edf2);
}
.pending-deletion-modal h3 {
  margin: 0 0 12px;
  color: #ef4444;
  font-size: 17px;
}
.pending-deletion-modal p {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text, #e8edf2);
}
.pending-deletion-modal .modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.pending-deletion-modal .btn,
.pending-deletion-modal .btn-danger {
  padding: 10px 16px;
  border-radius: var(--radius-sm, 6px);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
}
.pending-deletion-modal .btn {
  background: var(--accent, #1a8cff);
  color: white;
}
.pending-deletion-modal .btn:hover:not(:disabled) {
  background: var(--accent-hover, #1374d8);
}
.pending-deletion-modal .btn-danger {
  background: transparent;
  color: #ef4444;
  border: 1px solid rgba(220, 38, 38, 0.4);
}
.pending-deletion-modal .btn-danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.1);
}
.pending-deletion-modal .btn:disabled,
.pending-deletion-modal .btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pending-deletion-modal .error-msg-inline {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: rgba(220, 38, 38, 0.1);
  border-radius: 4px;
  color: #ef4444;
  font-size: 13px;
}
</style>
